// "Moves this minute" on the Mission Panel card (#6772, epic #6552; prototype mvBudgetHTML
// `:1408-1417`): one pip per move in the machine's budget, the ones left in the accent, then
// "X of 4 left"; at 0 left, the line that counts down to the server's `free_at`.
//
// EVERY NUMBER IS THE SERVER'S. The row's `moves` (`UtilBox.moves`, #6768's `movesView`) is the
// base: its `left + used` is the budget, never a literal. A move, a preview or a refusal answers
// later with `left` and `free_at` but no `used`; that answer is laid OVER the row
// ({@link effectiveMoves}) while it is newer than the row's read, and the budget stays the
// row's, so an overlaid `left` is never added to a stale `used`. The overlay gives way only when
// a read that started after it lands; at its `free_at` the page reads again rather than
// reverting (#6772 review).

import { MOVE_BUDGET_PER_MACHINE } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import type { UtilBox } from './fleetUtilization';
import { secondsUntil } from './time';

/** The row's reading: `UtilBox.moves`. */
export type MovesRow = NonNullable<UtilBox['moves']>;

/**
 * A later answer's `left` / `free_at`, and how fresh it is in the page's order (`orderStamp`):
 * a mutation's answer at the moment it settled, a preview's at the moment it started.
 */
export type MovesOverlay = {
  readonly left: number;
  readonly free_at: string | null;
  readonly order: number;
};

/** The meter as drawn: the pips (true = a move left), the counts and the 0-left wait. */
export type MeterView = {
  readonly pips: readonly boolean[];
  readonly left: number;
  readonly budget: number;
  /** When the next move frees, epoch ms; null when the server named no time. */
  readonly freeAtMs: number | null;
};

/** A row's budget: `left + used`, or the domain's per-machine budget without a row. */
export function rowBudget(row: MovesRow | null | undefined): number {
  return row == null ? MOVE_BUDGET_PER_MACHINE : row.left + row.used;
}

/**
 * The row with the overlay laid over it while the overlay is newer than the read (`readOrder`,
 * when the read's fetch started, in the page's order: never wall-clock ms, which ties): its `left` and `free_at`, `used` recomputed from the ROW's
 * budget. The overlay does NOT lapse at its `free_at` (#6772 review): the row under it may have
 * been read before the moves it counted, so the page reads the fleet again then
 * (`./move-budgets.ts`) and this keeps the overlay until that read lands. No row: undefined (an
 * api older than #6768).
 */
export function effectiveMoves(
  row: MovesRow | null | undefined,
  overlay: MovesOverlay | undefined,
  readOrder: number,
): MovesRow | undefined {
  if (row == null) return undefined;
  if (overlay === undefined || overlay.order < readOrder) return row;
  const budget = rowBudget(row);
  const left = Math.min(budget, Math.max(0, overlay.left));
  return { left, used: budget - left, free_at: overlay.free_at };
}

/** The meter, or null when the machine has used no move this minute (or reads no `moves`). */
export function meterView(moves: MovesRow | null | undefined): MeterView | null {
  if (moves == null || moves.used <= 0) return null;
  const budget = rowBudget(moves);
  const left = Math.max(0, Math.min(budget, moves.left));
  const free = moves.free_at === null ? Number.NaN : Date.parse(moves.free_at);
  return {
    pips: Array.from({ length: budget }, (_, i) => i < left),
    left,
    budget,
    freeAtMs: Number.isNaN(free) ? null : free,
  };
}

/** "X of 4 left". */
export function moveMeterLine(v: MeterView, tr: Translator): string {
  return tr('fleet.move.meter_left', { left: v.left, budget: v.budget });
}

/** "Each machine can send or take 4 moves a minute. An epic counts one move per job." */
export function moveMeterTitle(v: MeterView, tr: Translator): string {
  return tr('fleet.move.meter_title', { budget: v.budget });
}

/** At 0 left: "Move limit reached. <m> can send or take work again in N s."; else null. */
export function moveMeterOut(
  v: MeterView,
  name: string,
  nowMs: number,
  tr: Translator,
): string | null {
  if (v.left > 0) return null;
  return tr('fleet.move.meter_out', { name, seconds: secondsUntil(v.freeAtMs, nowMs) });
}
